import React, { useEffect, useState } from 'react';
import CSS from 'csstype';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// css
import '../../App.css';
import './collapsing-panel.css';

// images
import { SquareChevronLeft, SquareChevronRight } from 'lucide-react';

// classes

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import app state definition and functions (redux).
import { setPanelExpanded } from '../../app-slice';

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

export enum PanelPosition {
  LEFT,
  RIGHT,
}

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

//	-------------------------------------------------
//
//	A panel that becomes collapsable when the screen
//      width is below a certain threshold.
//
//	Parameters:
//
//		?: ?
//
//	-------------------------------------------------

export default function CollapsingPanel(props: {
  children?: any;
  className?: string;
  title?: string;
  hideTitle?: boolean;
  position: PanelPosition;
  size?: string;
  padding?: string;
  width?: string;
  height?: string;
  flex?: string;
  margin?: string;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  const sNarrowScreen: boolean = useAppSelector((state) => state.global.narrowScreen);

  const sPanelExpanded: boolean = useAppSelector((state) => state.app.panelExpanded);

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  const combinedClassName: string = ['text-colour', props.className ?? '']
    .filter(Boolean)
    .join(' ');

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	--------------------------------------------------------------------------

  if (sPanelExpanded === true && sNarrowScreen === true) {
    return (
      <div
        style={{
          flex: '0 0 51px',
          position: 'relative',
        }}
      >
        <div
          role="complementary"
          aria-label={props.title}
          className={combinedClassName}
          style={{
            ...{
              flex: props.flex === '1 1' ? '1 1' : '0 0 auto',
              height: props.height ?? 'auto',
              display: 'flex',
              flexDirection: 'column',
              margin: props.margin ?? '0px',
              padding: props.padding ?? '0px',
              alignItems: 'flex-end',
              position: 'absolute',
              top: '0px',
              zIndex: '2',
            },
            ...(props.position === PanelPosition.LEFT
              ? { left: '0px' }
              : { right: '0px' }),
          }}
        >
          {/* the maximised panel */}
          <div
            className="expanded-panel"
            data-maximised="T"
            style={{
              width: props.width ?? 'auto',
              height: props.height ?? 'auto',
              flex: props.flex !== undefined ? props.flex : '0 0 auto',
              padding: props.padding ?? '0px',
            }}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
              e.stopPropagation();
            }}
          >
            {/*
         //  panel title
         */}
            <div
              style={{
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                margin: '10px 10px 0px 10px',
              }}
            >
              {props.position === PanelPosition.RIGHT && (
                <button
                  type="button"
                  className="text-colour"
                  onClick={() => dispatch(setPanelExpanded(false))}
                  style={{
                    border: 'none',
                    background: 'transparent',
                  }}
                >
                  <SquareChevronRight width={30} height={30} strokeWidth={1.5} />
                </button>
              )}
              {props.position === PanelPosition.RIGHT && <div style={{ flex: '1 1' }} />}
              <div
                className="header-text"
                style={{ flex: '0 0 auto', margin: '0px 0px 0px 0px' }}
              >
                {props.title}
              </div>
              {props.position === PanelPosition.LEFT && <div style={{ flex: '1 1' }} />}
              {props.position === PanelPosition.LEFT && (
                <button
                  type="button"
                  className="text-colour"
                  onClick={() => dispatch(setPanelExpanded(false))}
                  style={{
                    border: 'none',
                    background: 'transparent',
                  }}
                >
                  <SquareChevronLeft width={30} height={30} strokeWidth={1.5} />
                </button>
              )}
            </div>
            <div style={{ flex: '0 0 10px' }} />

            {/*
         //  The children of this component go here.
         */}
            {props.children}
          </div>
        </div>
      </div>
    );
  } else if (sNarrowScreen === false) {
    return (
      <div
        role="complementary"
        aria-label={props.title}
        className={combinedClassName}
        style={{
          ...{
            flex: props.flex === '1 1' ? '1 1' : '0 0 auto',
            height: props.height ?? 'auto',
            display: 'flex',
            flexDirection: 'column',
            margin: props.margin ?? '0px',
            padding: props.padding ?? '0px',
            alignItems: 'flex-end',
            position: 'static',
            top: '0px',
            zIndex: '2',
          },
          ...(props.position === PanelPosition.LEFT ? { left: '0px' } : { right: '0px' }),
        }}
      >
        {/* the maximised panel */}
        <div
          className="expanded-panel"
          data-maximised="T"
          style={{
            width: props.width ?? 'auto',
            height: props.height ?? 'auto',
            flex: props.flex !== undefined ? props.flex : '0 0 auto',
            padding: props.padding ?? '0px',
          }}
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
          }}
        >
          {/*
         //  panel title
         */}
          {props.hideTitle !== true && (
            <div
              style={{
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                margin: '10px 10px 0px 10px',
              }}
            >
              <div
                className="header-text"
                style={{ flex: '1 1', margin: '0px 10px 0px 0px' }}
              >
                {props.title}
              </div>
            </div>
          )}
          <div style={{ flex: '0 0 10px' }} />

          {/*
         //  The children of this component go here.
         */}
          {props.children}
        </div>
      </div>
    );
  } else {
    return (
      <div
        role="complementary"
        aria-label={props.title}
        className={combinedClassName}
        style={{
          ...{
            flex: props.flex === '1 1' ? '1 1' : '0 0 auto',
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            margin: props.margin ?? '0px',
            padding: props.padding ?? '0px',
            alignItems: 'flex-end',
            position: 'static',
            top: '0px',
          },
          ...(props.position === PanelPosition.LEFT ? { left: '0px' } : { right: '0px' }),
        }}
      >
        {/*
       //  the minimised panel
       */}
        <button
          className="collapsed-panel text-colour"
          type="button"
          title="Maximise panel"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            dispatch(setPanelExpanded(true));
            e.stopPropagation();
          }}
          data-maximised="F"
        >
          {props.position === PanelPosition.LEFT && (
            <SquareChevronRight width={30} height={30} strokeWidth={1.5} />
          )}
          {props.position === PanelPosition.RIGHT && (
            <SquareChevronLeft width={30} height={30} strokeWidth={1.5} />
          )}
          <div className="rotated-text-box text-colour">{props.title}</div>
        </button>
      </div>
    );
  }

  //
} // CollapsingPanel
